import { useEffect, useRef, useState } from 'react';
import { X, Share2, Upload } from 'lucide-react';
import { REVENUE_TYPE_LABELS, autoRevenueType } from '../lib/revenueType.js';
import {
  apiPersons,
  apiRoles,
  apiDepartments,
  apiCountries,
  apiRegions,
  uploadAvatarFile,
} from '../api/client.js';
import Avatar from './Avatar.jsx';
import Modal from './Modal.jsx';
import DatePicker from './DatePicker.jsx';
import PersonManagersSection from './PersonManagersSection.jsx';
import WorkScheduleSection from './WorkScheduleSection.jsx';
import WorkDaysEditor, { DEFAULT_WORK_DAYS } from './WorkDaysEditor.jsx';
import TabbedEditor from './TabbedEditor.jsx';
import { hourlyToTjm, tjmToHourly } from '../lib/units.js';

/**
 * Sprint 14 — Éditeur de Person réutilisable (extrait de `PersonsView`).
 *
 * Utilisé par `PersonsView` (table admin) ET `HierarchyView` (clic sur un
 * nœud de l'organigramme côté ADMIN). Encapsule tout l'état du formulaire, le
 * chargement des référentiels (rôles / départements / pays / régions) et la
 * soumission create/update.
 *
 * Props :
 *  - open      : bool
 *  - personId  : string | null  (null ⇒ création)
 *  - onClose() : ferme la modale
 *  - onSaved(person) : callback après create/update réussi
 */

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const sectionTitle = {
  margin: '4px 0 6px',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const labelStyle = { display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' };

/**
 * Champ date **nullable**. Tant qu'aucune date n'est définie (`value === ''`),
 * on affiche un bouton « Aucune date » et PAS un `<input type="date">` vide :
 * Safari affiche la date du jour dans un champ date vide, ce qui laisse croire
 * à tort qu'une date est enregistrée. Cliquer révèle le sélecteur natif ; le ×
 * remet à vide (→ `null` au save). Source de vérité = `value` (contrôlé).
 */
function DateField({ value, onChange }) {
  const [editing, setEditing] = useState(Boolean(value));
  useEffect(() => {
    if (value) setEditing(true);
  }, [value]);

  if (!editing && !value) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        style={{ ...inputStyle, textAlign: 'left', color: 'var(--text-muted)', cursor: 'pointer' }}
        title="Cliquer pour définir une date"
      >
        Aucune date — définir…
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <DatePicker
        value={value}
        autoFocus={!value}
        onChange={(v) => onChange(v)}
        style={{ flex: 1 }}
      />
      <button
        type="button"
        onClick={() => {
          onChange('');
          setEditing(false);
        }}
        title="Effacer la date (aucune date enregistrée)"
        aria-label="Effacer la date"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-sm)',
          padding: 6,
          cursor: 'pointer',
          color: 'var(--danger)',
          flexShrink: 0,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  jobTitle: '',
  avatarUrl: '',
  roleId: '',
  departmentId: '',
  employeeType: 'EMPLOYEE',
  partTime: false,
  startDate: '',
  endDate: '',
  defaultHourlyRate: '',
  billRate: '',
  costRate: '',
  notes: '',
  workDaysHours: { ...DEFAULT_WORK_DAYS },
  countryCode: 'CH',
  regionCode: '',
  tags: [],
  // Phase 2.3.B — Référentiel Penta / CRM (synolog maître).
  smileId: '',
  pentaCrmId: '',
  smilePenta: '',
  bu: '',
  codeCollGescom: '',
  gescomContractId: '',
  gescomSyncDisabled: false,
  blRole: '',
  revenueType: '',
};


/**
 * Normalise les tags Person vers un tableau de chaînes (noms). Float renvoie
 * un tableau d'objets `{ name, type? }` ; certaines entrées peuvent être de
 * simples chaînes. On déduplique (insensible à la casse) en conservant le
 * premier libellé rencontré.
 */
function normalizeTags(tags) {
  if (!Array.isArray(tags)) return [];
  const seen = new Set();
  const out = [];
  for (const t of tags) {
    const name = typeof t === 'string' ? t : t && typeof t === 'object' ? String(t.name ?? '') : '';
    const trimmed = name.trim();
    if (trimmed && !seen.has(trimmed.toLowerCase())) {
      seen.add(trimmed.toLowerCase());
      out.push(trimmed);
    }
  }
  return out;
}

function toDateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * Normalise `workDaysHours` en objet par nom de jour `{ monday: 8, … }`.
 *
 * Float renvoie soit un **objet** par nom de jour, soit un **tableau** de 7
 * entrées aligné sur `Date.getDay()` : `[dim, lun, mar, mer, jeu, ven, sam]`
 * (même convention que `getWorkingDayMask` du Schedule). L'ancien code étalait
 * le tableau dans un objet → clés numériques ignorées → la modale retombait à
 * tort sur le défaut Lun-Ven (bug observé : Nicole `[0,0,8,8,8,8,0]` = Mar-Ven
 * affichée Lun-Ven). On convertit donc explicitement.
 */
function normalizeWorkDays(wdh) {
  if (!wdh) return { ...DEFAULT_WORK_DAYS };
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  if (Array.isArray(wdh)) {
    if (wdh.length !== 7) return { ...DEFAULT_WORK_DAYS };
    return {
      sunday: num(wdh[0]),
      monday: num(wdh[1]),
      tuesday: num(wdh[2]),
      wednesday: num(wdh[3]),
      thursday: num(wdh[4]),
      friday: num(wdh[5]),
      saturday: num(wdh[6]),
    };
  }
  if (typeof wdh === 'object') {
    const out = { ...DEFAULT_WORK_DAYS };
    for (const k of Object.keys(DEFAULT_WORK_DAYS)) {
      if (wdh[k] != null && Number.isFinite(Number(wdh[k]))) out[k] = Number(wdh[k]);
    }
    return out;
  }
  return { ...DEFAULT_WORK_DAYS };
}

function personToForm(p) {
  return {
    firstName: p.firstName,
    lastName: p.lastName,
    email: p.email ?? '',
    jobTitle: p.jobTitle ?? '',
    avatarUrl: p.avatarUrl ?? '',
    roleId: p.roleId ?? '',
    departmentId: p.departmentId ?? '',
    employeeType: p.employeeType ?? 'EMPLOYEE',
    partTime: Boolean(p.partTime),
    startDate: toDateInputValue(p.startDate),
    endDate: toDateInputValue(p.endDate),
    // Affichés en journalier (TJM/CJM = CHF/jour) : ×8 depuis le stockage horaire.
    defaultHourlyRate: hourlyToTjm(p.defaultHourlyRate ?? ''),
    billRate: hourlyToTjm(p.billRate ?? ''),
    costRate: hourlyToTjm(p.costRate ?? ''),
    notes: p.notes ?? '',
    workDaysHours: normalizeWorkDays(p.workDaysHours),
    countryCode: p.countryCode ?? 'CH',
    regionCode: p.regionCode ?? '',
    tags: normalizeTags(p.tags),
    // Phase 2.3.B — Référentiel Penta / CRM.
    smileId: p.smileId ?? '',
    pentaCrmId: p.pentaCrmId ?? '',
    smilePenta: p.smilePenta ?? '',
    bu: p.bu ?? '',
    codeCollGescom: p.codeCollGescom ?? '',
    gescomContractId: p.gescomContractId != null ? String(p.gescomContractId) : '',
    gescomSyncDisabled: Boolean(p.gescomSyncDisabled),
    blRole: p.blRole ?? '',
    revenueType: p.revenueType ?? '',
  };
}

/**
 * Éditeur de tags : chips supprimables + champ de saisie avec autocomplete
 * (datalist natif) sur les tags existants. Entrée ou « + Ajouter » valide ;
 * un tag déjà présent n'est pas dupliqué. Les suggestions non encore
 * sélectionnées sont affichées en chips cliquables pour réutilisation rapide.
 */
function TagsEditor({ selected, suggestions, onAdd, onRemove }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    onAdd(draft);
    setDraft('');
  };
  const selectedLower = new Set(selected.map((t) => t.toLowerCase()));
  const unused = suggestions.filter((s) => !selectedLower.has(s.toLowerCase()));

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'var(--glass)',
    border: '1px solid var(--glass-border)',
    borderRadius: 999,
    padding: '4px 6px 4px 10px',
    fontSize: 12,
    color: 'var(--text)',
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {selected.length > 0 ? (
          selected.map((t) => (
            <span key={t} style={chipStyle}>
              {t}
              <button
                type="button"
                onClick={() => onRemove(t)}
                title={`Retirer ${t}`}
                aria-label={`Retirer ${t}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: 0,
                }}
              >
                <X size={13} />
              </button>
            </span>
          ))
        ) : (
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Aucun tag</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          list="person-tags-suggestions"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          placeholder="Ajouter un tag (nouveau ou existant)…"
          maxLength={128}
          style={{ ...inputStyle, flex: 1 }}
        />
        <datalist id="person-tags-suggestions">
          {unused.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <button type="button" onClick={commit} disabled={!draft.trim()} style={secondaryBtn}>
          + Ajouter
        </button>
      </div>

      {unused.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Existants :</span>
          {unused.slice(0, 30).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onAdd(s)}
              title={`Ajouter ${s}`}
              style={{
                background: 'transparent',
                border: '1px dashed var(--glass-border)',
                borderRadius: 999,
                padding: '3px 10px',
                fontSize: 12,
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              + {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Champ d'upload d'avatar : preview (Avatar), bouton « Choisir une photo »
 * (upload presigned-PUT vers MinIO/S3) et fallback URL manuelle.
 *
 * P1 — on persiste l'URL publique NUE renvoyée par l'upload (pas de `?t=`).
 * Le bouton d'upload est désactivé tant que la Person n'existe pas (création) :
 * la clé S3 est dérivée du personId, donc on attend l'enregistrement.
 */
function AvatarUploadField({ personId, person, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-sélectionner le même fichier
    if (!file || !personId) return;
    setError(null);
    setUploading(true);
    try {
      const publicUrl = await uploadAvatarFile(personId, file);
      onChange(publicUrl); // URL nue, sans cache-bust
    } catch (err) {
      setError(err.message || "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={labelStyle}>Photo de profil</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Avatar person={{ ...(person ?? {}), avatarUrl: value }} size={56} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!personId || uploading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm, 6px)',
              border: '1px solid var(--glass-border)',
              background: 'var(--glass)',
              color: 'var(--text)',
              cursor: !personId || uploading ? 'not-allowed' : 'pointer',
              opacity: !personId || uploading ? 0.6 : 1,
              fontSize: 13,
            }}
          >
            <Upload size={14} />
            {uploading ? 'Envoi…' : 'Choisir une photo'}
          </button>
          {!personId ? (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Disponible après création.
            </span>
          ) : null}
        </div>
      </div>
      <div>
        <label style={{ ...labelStyle, fontSize: 12 }}>ou URL avatar (optionnel)</label>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={512}
          placeholder="https://…"
          style={inputStyle}
        />
      </div>
      {error ? (
        <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>
      ) : null}
    </div>
  );
}

/**
 * Champs éditables par un MANAGER (cohérent backend
 * `PERSON_MANAGER_EDITABLE_FIELDS` dans persons.routes.ts). Si `editableFields`
 * vaut `null` (défaut ADMIN), le formulaire complet est rendu.
 */
export const MANAGER_EDITABLE_FIELDS = ['jobTitle', 'avatarUrl', 'notes', 'tags'];

export default function PersonFormModal({
  open,
  personId,
  onClose,
  onSaved,
  editableFields = null,
}) {
  const restricted = Array.isArray(editableFields);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [buOptions, setBuOptions] = useState([]);
  // « Nouvelle BU… » : bascule la liste en saisie libre (normalisée côté API).
  const [buCustom, setBuCustom] = useState(false);

  const [editing, setEditing] = useState(null); // Person complète (mode édition)
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // (Re)chargement à l'ouverture : référentiels + Person si édition.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setFormError(null);
    setLoading(true);
    (async () => {
      try {
        const [r, d, c, t, b] = await Promise.all([
          apiRoles.list('active'),
          apiDepartments.list('active'),
          apiCountries.list('active'),
          apiPersons.tags().catch(() => ({ tags: [] })),
          apiPersons.bus().catch(() => ({ bus: [] })),
        ]);
        if (cancelled) return;
        setRoles(r.items);
        setDepartments(d.items);
        setCountries(c.items);
        setTagSuggestions(Array.isArray(t.tags) ? t.tags : []);
        setBuOptions(Array.isArray(b.bus) ? b.bus : []);
        setBuCustom(false);

        if (personId) {
          const p = await apiPersons.get(personId);
          if (cancelled) return;
          setEditing(p);
          setForm(personToForm(p));
        } else {
          setEditing(null);
          setForm(EMPTY);
        }
      } catch (err) {
        if (!cancelled) setFormError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, personId]);

  // Recharge les régions à chaque changement de pays sélectionné.
  useEffect(() => {
    if (!open || !form.countryCode) {
      setRegions([]);
      return undefined;
    }
    let cancelled = false;
    apiRegions
      .list(form.countryCode, 'active')
      .then(({ items: data }) => {
        if (!cancelled) setRegions(data);
      })
      .catch(() => {
        if (!cancelled) setRegions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, form.countryCode]);

  const onCountryChange = (newCountryCode) => {
    setForm((prev) =>
      prev.countryCode === newCountryCode
        ? prev
        : { ...prev, countryCode: newCountryCode, regionCode: '' },
    );
  };

  const onWorkHourChange = (dayKey, value) => {
    const n = value === '' ? 0 : Number(value);
    setForm((prev) => ({
      ...prev,
      workDaysHours: { ...prev.workDaysHours, [dayKey]: Number.isFinite(n) ? n : 0 },
    }));
  };

  const addTag = (raw) => {
    const name = String(raw ?? '').trim();
    if (!name) return;
    setForm((prev) => {
      const exists = prev.tags.some((t) => t.toLowerCase() === name.toLowerCase());
      return exists ? prev : { ...prev, tags: [...prev.tags, name] };
    });
  };

  const removeTag = (name) => {
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t.toLowerCase() !== name.toLowerCase()),
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const tagsPayload = normalizeTags(form.tags).map((name) => ({ name }));
      let payload;
      if (restricted) {
        // MANAGER : on n'envoie QUE les champs autorisés (le backend rejette
        // tout champ hors liste en 403). Pas de création en mode restreint.
        const partial = {
          jobTitle: form.jobTitle.trim() || null,
          avatarUrl: form.avatarUrl.trim() || null,
          notes: form.notes.trim() || null,
          tags: tagsPayload,
        };
        payload = Object.fromEntries(
          Object.entries(partial).filter(([k]) => editableFields.includes(k)),
        );
      } else {
        // Saisie en journalier (TJM/CJM) → stockage horaire (÷8).
        const rateStr = String(tjmToHourly(form.defaultHourlyRate) ?? '').trim();
        const billStr = String(tjmToHourly(form.billRate) ?? '').trim();
        const costStr = String(tjmToHourly(form.costRate) ?? '').trim();
        payload = {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim() || null,
          jobTitle: form.jobTitle.trim() || null,
          avatarUrl: form.avatarUrl.trim() || null,
          roleId: form.roleId || null,
          departmentId: form.departmentId || null,
          employeeType: form.employeeType,
          partTime: form.partTime,
          startDate: form.startDate || null,
          endDate: form.endDate || null,
          defaultHourlyRate: rateStr === '' ? null : rateStr,
          billRate: billStr === '' ? null : billStr,
          costRate: costStr === '' ? null : costStr,
          notes: form.notes.trim() || null,
          // En création : la disponibilité initiale est envoyée ici (le service
          // seed l'historique). En édition : la disponibilité est gérée par les
          // versions datées (endpoint /work-schedule), on ne l'écrase pas ici.
          ...(editing ? {} : { workDaysHours: form.workDaysHours }),
          countryCode: form.countryCode || 'CH',
          regionCode: form.regionCode || null,
          tags: tagsPayload,
          // Phase 2.3.B — Référentiel Penta / CRM (synolog maître).
          smileId: form.smileId.trim() || null,
          pentaCrmId: form.pentaCrmId.trim() || null,
          smilePenta: form.smilePenta.trim() || null,
          bu: form.bu.trim() || null,
          codeCollGescom: form.codeCollGescom.trim() || null,
          gescomContractId: /^\d+$/.test(form.gescomContractId.trim())
            ? Number(form.gescomContractId.trim())
            : null,
          gescomSyncDisabled: form.gescomSyncDisabled,
          blRole: form.blRole || null,
          revenueType: form.revenueType || null,
        };
      }
      const saved = editing
        ? await apiPersons.update(editing.id, payload)
        : await apiPersons.create(payload);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={personId ? 'Modifier la personne' : 'Nouvelle personne'}
      footer={
        <>
          <button type="button" onClick={onClose} style={secondaryBtn}>
            Annuler
          </button>
          <button
            type="submit"
            form="person-form"
            disabled={submitting || loading || !form.firstName.trim() || !form.lastName.trim()}
            style={{ ...primaryBtn, opacity: submitting || loading ? 0.6 : 1 }}
          >
            Enregistrer
          </button>
        </>
      }
    >
      {loading ? (
        <div style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : (
        <form id="person-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 16 }}>
          {restricted ? (
            <>
              {editing ? (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    background: 'var(--glass)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '8px 10px',
                  }}
                >
                  {editing.firstName} {editing.lastName}
                  {editing.email ? ` · ${editing.email}` : ''}
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    En tant que manager, vous pouvez modifier le poste, l'avatar, les notes et
                    les tags.
                  </div>
                </div>
              ) : null}
              {editableFields.includes('jobTitle') || editableFields.includes('avatarUrl') ? (
                <div>
                  <div style={sectionTitle}>Identité</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                    {editableFields.includes('jobTitle') ? (
                      <div>
                        <label style={labelStyle}>Job title</label>
                        <input
                          type="text"
                          value={form.jobTitle}
                          onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
                          maxLength={128}
                          style={inputStyle}
                        />
                      </div>
                    ) : null}
                    {editableFields.includes('avatarUrl') ? (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <AvatarUploadField
                          personId={editing?.id ?? null}
                          person={editing}
                          value={form.avatarUrl}
                          onChange={(v) => setForm({ ...form, avatarUrl: v })}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {editableFields.includes('notes') ? (
                <div>
                  <div style={sectionTitle}>Notes</div>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    maxLength={10000}
                    style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
              ) : null}
              {editableFields.includes('tags') ? (
                <div>
                  <div style={sectionTitle}>Tags</div>
                  <TagsEditor
                    selected={form.tags}
                    suggestions={tagSuggestions}
                    onAdd={addTag}
                    onRemove={removeTag}
                  />
                </div>
              ) : null}
              {/* Disponibilité datée — éditable par le MANAGER sur sa descendance
                  (endpoint dédié, indépendant des champs PATCH restreints). */}
              {editing ? (
                <WorkScheduleSection personId={editing.id} canEdit />
              ) : null}
              {formError ? (
                <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div>
              ) : null}
            </>
          ) : (
          <>
          {/* Gouvernance de la donnée — bandeau référentiel fédéré Synotis */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 12px',
              background: 'var(--glass)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 12.5,
              lineHeight: 1.45,
              color: 'var(--text-muted)',
            }}
          >
            <Share2 size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--accent)' }} />
            <div>
              <strong style={{ color: 'var(--text)' }}>Référentiel Synotis</strong> — Synolog est{' '}
              <em>maître</em> de l'identité (et des champs Penta/CRM) de cette personne. Les
              modifications sont publiées au référentiel central partagé. Le{' '}
              <em>tarif de référence</em> (CJM/TJM) est géré par Synosight et affiché en
              lecture seule dans l’onglet « Contrat &amp; Taux ».
            </div>
          </div>

          <TabbedEditor
            tabs={[
              {
                key: 'identite',
                label: 'Identité',
                content: (
                  <div style={{ display: 'grid', gap: 16 }}>
                    {/* Identité */}
                    <div>
                      <div style={sectionTitle}>Identité</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Prénom</label>
                          <input
                            type="text"
                            value={form.firstName}
                            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                            required
                            maxLength={128}
                            autoFocus
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Nom</label>
                          <input
                            type="text"
                            value={form.lastName}
                            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                            required
                            maxLength={128}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Email</label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            maxLength={255}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Job title</label>
                          <input
                            type="text"
                            value={form.jobTitle}
                            onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
                            maxLength={128}
                            style={inputStyle}
                          />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <AvatarUploadField
                            personId={editing?.id ?? null}
                            person={editing}
                            value={form.avatarUrl}
                            onChange={(v) => setForm({ ...form, avatarUrl: v })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Affectation */}
                    <div>
                      <div style={sectionTitle}>Affectation</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Rôle</label>
                          <select value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })} style={inputStyle}>
                            <option value="">— Aucun —</option>
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Département</label>
                          <select
                            value={form.departmentId}
                            onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
                            style={inputStyle}
                          >
                            <option value="">— Aucun —</option>
                            {departments.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Type d'emploi</label>
                          <select
                            value={form.employeeType}
                            onChange={(e) => setForm({ ...form, employeeType: e.target.value })}
                            style={inputStyle}
                          >
                            <option value="EMPLOYEE">Salarié</option>
                            <option value="CONTRACTOR">Sous-traitant</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Temps partiel</label>
                          {/* Autorise la personne à déclarer elle-même ses jours de
                              temps partiel dans le planning (validés d'office). */}
                          <div style={{ display: 'flex', gap: 8 }}>
                            {[
                              { value: false, label: 'Non' },
                              { value: true, label: 'Oui' },
                            ].map((opt) => {
                              const active = form.partTime === opt.value;
                              return (
                                <button
                                  key={opt.label}
                                  type="button"
                                  onClick={() => setForm({ ...form, partTime: opt.value })}
                                  title={
                                    opt.value
                                      ? 'La personne peut déclarer ses jours de temps partiel'
                                      : 'Pas de temps partiel déclarable par la personne'
                                  }
                                  style={{
                                    padding: '6px 16px',
                                    borderRadius: 999,
                                    border: '1px solid',
                                    borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
                                    background: active ? 'var(--accent)' : 'transparent',
                                    color: active ? '#fff' : 'var(--text-muted)',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>Pays</label>
                          <select value={form.countryCode} onChange={(e) => onCountryChange(e.target.value)} required style={inputStyle}>
                            {countries.length === 0 ? (
                              <option value="CH">CH — Suisse (default)</option>
                            ) : (
                              countries.map((c) => (
                                <option key={c.code} value={c.code}>
                                  {c.code} — {c.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Région (optionnelle)</label>
                          <select
                            value={form.regionCode}
                            onChange={(e) => setForm({ ...form, regionCode: e.target.value })}
                            disabled={regions.length === 0}
                            style={inputStyle}
                          >
                            <option value="">{regions.length === 0 ? '— Aucune région disponible —' : '— Aucune —'}</option>
                            {regions.map((r) => (
                              <option key={r.code} value={r.code}>
                                {r.code} — {r.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Tags (éditables — création ou réutilisation) */}
                    <div>
                      <div style={sectionTitle}>Tags</div>
                      <TagsEditor
                        selected={form.tags}
                        suggestions={tagSuggestions}
                        onAdd={addTag}
                        onRemove={removeTag}
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: 'contrat',
                label: 'Contrat & Taux',
                content: (
                  <div style={{ display: 'grid', gap: 16 }}>
                    {/* Période */}
                    <div>
                      <div style={sectionTitle}>Période d'emploi</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Date de début</label>
                          <DateField
                            value={form.startDate}
                            onChange={(v) => setForm({ ...form, startDate: v })}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Date de fin</label>
                          <DateField
                            value={form.endDate}
                            onChange={(v) => setForm({ ...form, endDate: v })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Taux */}
                    <div>
                      <div style={sectionTitle}>Taux</div>

                      {/* Tarif de référence Synosight (lecture seule, valeur principale) */}
                      <div
                        style={{
                          marginBottom: 12,
                          padding: '10px 12px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--glass)',
                          border: '1px solid var(--glass-border)',
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                          Tarif de référence — Synosight (lecture seule)
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                          <div>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>TJM de référence</span>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                              {editing?.referenceRate?.referenceTjm != null
                                ? `${Number(editing.referenceRate.referenceTjm).toFixed(2)} CHF/j`
                                : '—'}
                            </div>
                          </div>
                          <div>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>CJM de référence</span>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                              {editing?.referenceRate?.referenceCjm != null
                                ? `${Number(editing.referenceRate.referenceCjm).toFixed(2)} CHF/j`
                                : '—'}
                            </div>
                          </div>
                        </div>
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                          {editing?.referenceRate?.referenceTjm == null && editing?.referenceRate?.referenceCjm == null
                            ? 'Aucun tarif publié au référentiel central pour cette personne (smileId absent ou non synchronisé).'
                            : 'Valeur maître publiée par Synosight. Le TJM local ci-dessous n’est qu’un override optionnel.'}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        <div>
                          <label style={labelStyle}>TJM local (override, CHF/jour)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.billRate}
                            onChange={(e) => setForm({ ...form, billRate: e.target.value })}
                            placeholder={
                              editing?.referenceRate?.referenceTjm != null
                                ? `réf. Synosight : ${Number(editing.referenceRate.referenceTjm).toFixed(2)}`
                                : form.defaultHourlyRate
                                  ? `défaut Float : ${form.defaultHourlyRate}`
                                  : ''
                            }
                            style={inputStyle}
                          />
                          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                            Override local optionnel (prioritaire sur la réf. si renseigné). 1 jour = 8h.
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>CJM (CHF/jour)</label>
                          <input type="number" step="0.01" min="0" value={form.costRate} onChange={(e) => setForm({ ...form, costRate: e.target.value })} style={inputStyle} />
                          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                            Coût journalier moyen (salaire + charges) ou tarif sous-traitant.
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>TJM Float (importé)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.defaultHourlyRate}
                            onChange={(e) => setForm({ ...form, defaultHourlyRate: e.target.value })}
                            style={inputStyle}
                          />
                          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                            Valeur importée de Float (fallback si TJM vide).
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <div style={sectionTitle}>Notes</div>
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={3}
                        maxLength={10000}
                        style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: 'disponibilite',
                label: 'Disponibilité',
                content: (
                  <div style={{ display: 'grid', gap: 16 }}>
                    {editing ? (
                      <WorkScheduleSection
                        personId={editing.id}
                        canEdit
                        onCurrentChange={(wdh) => setForm((f) => ({ ...f, workDaysHours: wdh }))}
                      />
                    ) : (
                      <div>
                        <div style={sectionTitle}>Disponibilité (heures/jour)</div>
                        <WorkDaysEditor value={form.workDaysHours} onChange={onWorkHourChange} />
                        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                          Cette disponibilité initiale pourra être historisée (nouvelles versions
                          datées) une fois la personne créée.
                        </div>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'hierarchie',
                label: 'Hiérarchie',
                content: (
                  <div style={{ display: 'grid', gap: 16 }}>
                    {editing ? (
                      <PersonManagersSection personId={editing.id} canEdit />
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        La hiérarchie managériale se gère après la création de la personne.
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'penta',
                label: 'Référentiel Penta / CRM',
                content: (
                  <div style={{ display: 'grid', gap: 16 }}>
                    {/* Référentiel Penta / CRM — Phase 2.3.B (synolog maître de l'identité Penta) */}
                    <div>
                      <div style={sectionTitle}>Référentiel Penta / CRM</div>
                      <div
                        style={{
                          margin: '-2px 0 8px',
                          fontSize: 11.5,
                          color: 'var(--accent)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        <Share2 size={12} />
                        Maître : Synolog · publié au référentiel central
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Gescom ID</label>
                          <input
                            type="text"
                            value={form.smileId}
                            onChange={(e) => setForm({ ...form, smileId: e.target.value })}
                            maxLength={64}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Cebelian ID (Obsolète)</label>
                          <input
                            type="text"
                            value={form.pentaCrmId}
                            onChange={(e) => setForm({ ...form, pentaCrmId: e.target.value })}
                            maxLength={64}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Gescom Penta</label>
                          <input
                            type="text"
                            value={form.smilePenta}
                            onChange={(e) => setForm({ ...form, smilePenta: e.target.value })}
                            maxLength={64}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>BU</label>
                          {buCustom ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input
                                type="text"
                                value={form.bu}
                                onChange={(e) => setForm({ ...form, bu: e.target.value })}
                                maxLength={128}
                                placeholder="nouvelle BU (ex. bl data management)"
                                autoFocus
                                style={{ ...inputStyle, flex: 1 }}
                              />
                              <button
                                type="button"
                                onClick={() => { setBuCustom(false); setForm({ ...form, bu: editing?.bu ?? '' }); }}
                                title="Revenir à la liste des BU"
                                style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <select
                              value={form.bu}
                              onChange={(e) => {
                                if (e.target.value === '__new__') { setBuCustom(true); setForm({ ...form, bu: '' }); }
                                else setForm({ ...form, bu: e.target.value });
                              }}
                              style={inputStyle}
                            >
                              <option value="">(aucune)</option>
                              {[...new Set([...buOptions, ...(form.bu && !buOptions.includes(form.bu) ? [form.bu] : [])])].map((b) => (
                                <option key={b} value={b}>{b}</option>
                              ))}
                              <option value="__new__">Nouvelle BU…</option>
                            </select>
                          )}
                        </div>
                        <div>
                          <label style={labelStyle}>Gescom code Collaborateur</label>
                          <input
                            type="text"
                            value={form.codeCollGescom}
                            onChange={(e) => setForm({ ...form, codeCollGescom: e.target.value })}
                            maxLength={64}
                            style={inputStyle}
                          />
                        </div>
                        {form.employeeType === 'CONTRACTOR' && (
                          <div>
                            <label style={labelStyle}>Gescom n° de contrat sous-traitant</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={form.gescomContractId}
                              onChange={(e) => setForm({ ...form, gescomContractId: e.target.value.replace(/\D/g, '') })}
                              maxLength={10}
                              placeholder="Auto : dernier contrat Gescom"
                              title="ID_Acte du contrat nominatif dans Gescom. Vide : l'export reprend le dernier contrat utilisé par ce code collaborateur."
                              style={inputStyle}
                            />
                          </div>
                        )}
                        <div>
                          <label style={labelStyle}>Synchronisation Gescom</label>
                          {/* « Non synchronisé » : consultants Smile France dont le
                              temps arrive déjà dans Gescom par un projet jumeau. */}
                          <div style={{ display: 'flex', gap: 8 }}>
                            {[
                              { value: false, label: 'Synchronisé' },
                              { value: true, label: 'Non synchronisé' },
                            ].map((opt) => {
                              const active = form.gescomSyncDisabled === opt.value;
                              return (
                                <button
                                  key={opt.label}
                                  type="button"
                                  onClick={() => setForm({ ...form, gescomSyncDisabled: opt.value })}
                                  title={
                                    opt.value
                                      ? 'Saisies jamais exportées vers Gescom (projet jumeau sur une autre entité)'
                                      : 'Saisies exportées vers Gescom'
                                  }
                                  style={{
                                    padding: '6px 16px',
                                    borderRadius: 999,
                                    border: '1px solid',
                                    borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
                                    background: active ? 'var(--accent)' : 'transparent',
                                    color: active ? '#fff' : 'var(--text-muted)',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>Rôle dans la BL</label>
                          {/* #21 synosight — filtre de périmètre des indicateurs BL.
                              « Auto » : manager déduit des personnes encadrées. */}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {[
                              { value: '', label: 'Auto', title: 'Consultant, ou manager s\'il encadre au moins une personne' },
                              { value: 'MANAGER', label: 'Manager', title: 'Manager de la BL' },
                              { value: 'DIRECTOR', label: 'Directeur', title: 'Directeur de la BL' },
                            ].map((opt) => {
                              const active = form.blRole === opt.value;
                              return (
                                <button
                                  key={opt.label}
                                  type="button"
                                  onClick={() => setForm({ ...form, blRole: opt.value })}
                                  title={opt.title}
                                  style={{
                                    padding: '6px 16px',
                                    borderRadius: 999,
                                    border: '1px solid',
                                    borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
                                    background: active ? 'var(--accent)' : 'transparent',
                                    color: active ? '#fff' : 'var(--text-muted)',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>Type de revenu</label>
                          {/* #21 synosight — shadow P&L : Interne (dans le TACE), Intercompanie
                              (sous-traitant Smile), External (autre sous-traitant). */}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {[
                              { value: '', label: `Auto (${REVENUE_TYPE_LABELS[autoRevenueType(form.employeeType, form.email)]})`, title: 'Email @smile.fr / @smile-suisse.com → Intercompanie ; autre sous-traitant → External ; sinon Interne' },
                              { value: 'INTERNAL', label: 'Interne Synotis CH', title: 'Salarié Synotis CH, compté dans le TACE' },
                              { value: 'INTERCO', label: 'Intercompanie', title: 'Sous-traitant Smile (France ou Suisse) : revenu intercompanie, hors TACE' },
                              { value: 'EXTERNAL', label: 'External', title: 'Autre sous-traitant : external revenue, hors TACE' },
                            ].map((opt) => {
                              const active = form.revenueType === opt.value;
                              return (
                                <button
                                  key={opt.value || 'auto'}
                                  type="button"
                                  onClick={() => setForm({ ...form, revenueType: opt.value })}
                                  title={opt.title}
                                  style={{
                                    padding: '6px 16px',
                                    borderRadius: 999,
                                    border: '1px solid',
                                    borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
                                    background: active ? 'var(--accent)' : 'transparent',
                                    color: active ? '#fff' : 'var(--text-muted)',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
          />

          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
          </>
          )}
        </form>
      )}
    </Modal>
  );
}
